/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import React, { Component } from 'react';
import ErrorBoundary from '../containers/ErrorBoundary/ErrorBoundary';
import { IntlProvider } from 'react-intl';
import { Provider } from 'react-redux';
import ConfigService from '../services/ConfigService';
import FloodgateService from '../services/FloodgateService';
import FileTypeService from '../services/FileTypeService';
import './SearchProvider.scss';
import { getAppLocale } from '@intl/adobe-locales';
import '../utils/Globals';
import { LocaleUtils } from '../utils/LocaleUtils';
import store from '../store';
import { SearchBarViewProps } from '../views/SearchBarView';
import { SearchResultsPageViewProps } from '../views/SearchResultsPageView';

type SearchProviderProps = (SearchBarViewProps | SearchResultsPageViewProps) & {
	logger: Logger;
	children?: React.ReactElement;
	showErrorUI: boolean;
};
class SearchProvider extends Component<SearchProviderProps> {
	state: Record<string, any> = { loaded: false };
	async componentDidMount() {
		const floodgateService = new FloodgateService(this.props.searchHostContext);
		if (this.props.searchHostContext.featureFlags.includes('ccsearch_enable_floodgate_service')) {
			// Launch Floodgate service to get and store user features test list
			await floodgateService.loadFloodgateFeatures();
		} else {
			// if we don't have the feature flag enabled, delete any floodgate entries in local storage
			floodgateService.deleteFloodgateFeaturesLocalStorage();
		}
		// FileTypeService now serves dynamic translations too (localised strings from config).
		// Needed by UI as early as possible
		const fileTypeService = new FileTypeService(
			this.props.searchHostContext.featureFlags,
			this.props.searchHostContext,
		);
		fileTypeService.fetchFileTypeData(this.props.searchHostContext.locale);

		// Kick off call to config service -- we don't want to hold off the UI, especially for SearchBar.
		// We hand it the store so we can update the UI once the config has returned.
		const configService = new ConfigService(this.props.searchHostContext, store);
		configService.fetchConfig();

		this.setState({ loaded: true });
	}

	/**
	 * getAppLocale function Takes a locale (e.g. en, en-US, or en_US) and return a language code
	 * that can be used to load the corresponding translated messages.
	 * @param {String} locale - locale to map to an appropriate language code
	 * @returns {String} language code that can be used to get the right translated messages
	 */
	render() {
		const { loaded } = this.state;
		const appLocale = getAppLocale('ccsearch', this.props.searchHostContext.locale, 'en');
		const component = (
			<IntlProvider locale={appLocale} messages={LocaleUtils.getLocaleStrings(appLocale)}>
				<Provider store={store}>
					<ErrorBoundary
						locale={this.props.searchHostContext.locale}
						showErrorUI={this.props.showErrorUI}
						onErrorCallback={this.props.searchHostContext?.onError}
					>
						{loaded ? React.cloneElement(this.props.children, { ...this.props }) : null}
					</ErrorBoundary>
				</Provider>
			</IntlProvider>
		);
		return component;
	}
}

export default SearchProvider;
